import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { map, Observable } from "rxjs";
import { APP_CONFIG } from "../../../core/config/app-config";
import {
    CompanyReport,
    ReportEventType,
    ReportsPage,
} from "../models/report.models";

interface ActivityResponse {
    _id?: string;
    gmvid?: string;
    objectId?: string;
    username?: string;
    event?: {
        eventId?: string;
        eventType?: string;
        callId?: string;
        caller?: string;
        callee?: string;
        event?: string;
        executedWorkflowId?: string;
        workflowTitle?: string;
    };
}

@Injectable({ providedIn: "root" })
export class ReportsApiService {
    private readonly http = inject(HttpClient);
    private readonly config = inject(APP_CONFIG);
    private readonly reportsUrl = `${this.config.apiBaseUrl}/servlogs`;

    loadCompanyReports(
        clientId: string,
        amount: number,
        token?: string
    ): Observable<ReportsPage> {
        let params = new HttpParams().set("pageLength", amount);
        if (token) params = params.set("latestId", token);
        return this.http
            .get<
                ActivityResponse[]
            >(`${this.reportsUrl}/combined/logs/${encodeURIComponent(clientId)}`, { params })
            .pipe(
                map((items) => {
                    const reports = items.map((item) => this.mapReport(item));
                    return {
                        reports,
                        nextToken:
                            reports.length === amount
                                ? (reports.at(-1)?.objectId ?? null)
                                : null,
                    };
                })
            );
    }

    download(report: CompanyReport): Observable<Blob> {
        const target = this.downloadTarget(report);
        const headers = new HttpHeaders({
            Accept: "*/*",
            "Accept-Language": navigator.language || "en-US",
            "x-timezone": Intl.DateTimeFormat().resolvedOptions().timeZone,
        });
        return target.method === "POST"
            ? this.http.post(target.url, {}, { headers, responseType: "blob" })
            : this.http.get(target.url, { headers, responseType: "blob" });
    }

    filename(report: CompanyReport): string {
        const prefix =
            report.eventType === "VideoCall"
                ? "call"
                : report.eventType.toLocaleLowerCase();
        return `${prefix}_report_${this.reportTargetId(report)}.pdf`;
    }

    private downloadTarget(report: CompanyReport): {
        method: "GET" | "POST";
        url: string;
    } {
        const id = encodeURIComponent(this.reportTargetId(report));
        if (report.eventType === "VideoCall")
            return {
                method: "GET",
                url: `${this.reportsUrl}/calls/report/${id}`,
            };
        if (report.eventType === "Workflow")
            return {
                method: "GET",
                url: `${this.reportsUrl}/workflows/report/${id}`,
            };
        return {
            method: "POST",
            url: `${this.reportsUrl}/form_log/report/${id}`,
        };
    }

    private reportTargetId(report: CompanyReport): string {
        if (report.eventType === "VideoCall") return report.callId;
        return report.executedWorkflowId || report.eventId;
    }

    private mapReport(response: ActivityResponse): CompanyReport {
        const id = response.gmvid || response.objectId || response._id || "";
        const event = response.event ?? {};
        return {
            id,
            objectId: id,
            username: response.username ?? "",
            eventType: (event.eventType ?? "Workflow") as ReportEventType,
            eventName: event.event ?? "",
            callId: event.callId ?? "",
            caller: event.caller ?? "",
            callee: event.callee ?? "",
            eventId: event.eventId ?? "",
            executedWorkflowId: event.executedWorkflowId ?? "",
            workflowTitle: event.workflowTitle ?? "",
            createdAt: this.objectIdDate(id),
        };
    }

    private objectIdDate(value: string): Date | null {
        if (!/^[a-f\d]{24}$/iu.test(value)) return null;
        const date = new Date(Number.parseInt(value.slice(0, 8), 16) * 1000);
        return Number.isNaN(date.getTime()) ? null : date;
    }
}
